import React from 'react';
import { Box, Typography } from '@mui/material';
import { DS } from '../../PatientForms/designTokens';
import { PreviousMedication } from '../types';

interface HasAdditionalPreviousMedicationProps {
  additionalPreviousMedications: PreviousMedication[];
  setAdditionalPreviousMedications: (medications: PreviousMedication[]) => void;
  handleFormSubmit: (e: React.FormEvent) => void;
}

const HasAdditionalPreviousMedication: React.FC<
  HasAdditionalPreviousMedicationProps
> = ({
  additionalPreviousMedications,
  setAdditionalPreviousMedications,
  handleFormSubmit,
}) => {
  const handleInputChange = (
    index: number,
    field: keyof PreviousMedication,
    value: string
  ) => {
    const updatedMedications = [...additionalPreviousMedications];
    updatedMedications[index][field] = value;
    setAdditionalPreviousMedications(updatedMedications);
  };

  const handleAddNewSet = () => {
    setAdditionalPreviousMedications([
      ...additionalPreviousMedications,
      {
        name: '',
        dose: '',
        frequency: '',
        startYear: '',
        stopReason: '',
        sideEffects: '',
        stopReasonDetails: '',
        stopYear: '',
      },
    ]);
  };

  return (
    <Box>
      {additionalPreviousMedications.map((medication, index) => (
        // eslint-disable-next-line react/no-array-index-key -- entries have no id; the list is only appended to and edited in place
        <Box key={index}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mt: 2,
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Smärtlindrande läkemedel {index + 2}
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  display: 'block',
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Vilket läkemedel?
              </Typography>
              <input
                type="text"
                onBlur={handleFormSubmit}
                value={medication.name}
                onChange={(e) =>
                  handleInputChange(index, 'name', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontFamily: DS.fontBody,
                }}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  display: 'block',
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Vilken dos?
              </Typography>
              <input
                type="text"
                onBlur={handleFormSubmit}
                value={medication.dose}
                onChange={(e) =>
                  handleInputChange(index, 'dose', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontFamily: DS.fontBody,
                }}
              />
            </Box>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography
                sx={{
                  display: 'block',
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Hur ofta tog du medicinen?
              </Typography>
              <Box
                component="label"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <input
                  type="radio"
                  onBlur={handleFormSubmit}
                  value="mindre än 1 dag i veckan"
                  checked={medication.frequency === 'mindre än 1 dag i veckan'}
                  onChange={(e) =>
                    handleInputChange(index, 'frequency', e.target.value)
                  }
                  style={{ marginRight: '8px' }}
                />
                mindre än 1 dag i veckan
              </Box>
              <Box
                component="label"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <input
                  type="radio"
                  onBlur={handleFormSubmit}
                  value="1-2 dagar i veckan"
                  checked={medication.frequency === '1-2 dagar i veckan'}
                  onChange={(e) =>
                    handleInputChange(index, 'frequency', e.target.value)
                  }
                  style={{ marginRight: '8px' }}
                />
                1-2 dagar i veckan
              </Box>
              <Box
                component="label"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <input
                  type="radio"
                  onBlur={handleFormSubmit}
                  value="3-5 dagar i veckan"
                  checked={medication.frequency === '3-5 dagar i veckan'}
                  onChange={(e) =>
                    handleInputChange(index, 'frequency', e.target.value)
                  }
                  style={{ marginRight: '8px' }}
                />
                3-5 dagar i veckan
              </Box>
              <Box
                component="label"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <input
                  type="radio"
                  onBlur={handleFormSubmit}
                  name="previousMedicationFrequency"
                  value="mer än 5 dagar i veckan"
                  checked={medication.frequency === 'mer än 5 dagar i veckan'}
                  onChange={(e) =>
                    handleInputChange(index, 'frequency', e.target.value)
                  }
                  style={{ marginRight: '8px' }}
                />
                mer än 5 dagar i veckan
              </Box>
            </Box>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                display: 'block',
                color: '#374151',
                mb: 1,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Startår:
            </Typography>
            <input
              type="text"
              onBlur={handleFormSubmit}
              value={medication.startYear}
              onChange={(e) =>
                handleInputChange(index, 'startYear', e.target.value)
              }
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '16px',
                fontFamily: DS.fontBody,
              }}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography
              sx={{
                display: 'block',
                color: '#374151',
                mb: 1,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Varför slutade du med medicinen?
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box
                component="label"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <input
                  type="radio"
                  onBlur={handleFormSubmit}
                  value="Bristande effekt"
                  checked={medication.stopReason === 'Bristande effekt'}
                  onChange={(e) =>
                    handleInputChange(index, 'stopReason', e.target.value)
                  }
                  style={{ marginRight: '8px' }}
                />
                Bristande effekt
              </Box>
              <Box
                component="label"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <input
                  type="radio"
                  onBlur={handleFormSubmit}
                  value="Biverkan"
                  checked={medication.stopReason === 'Biverkan'}
                  onChange={(e) =>
                    handleInputChange(index, 'stopReason', e.target.value)
                  }
                  style={{ marginRight: '8px' }}
                />
                Biverkan
              </Box>
              <Box
                component="label"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <input
                  type="radio"
                  onBlur={handleFormSubmit}
                  value="Annat skäl"
                  checked={medication.stopReason === 'Annat skäl'}
                  onChange={(e) =>
                    handleInputChange(index, 'stopReason', e.target.value)
                  }
                  style={{ marginRight: '8px' }}
                />
                Annat skäl
              </Box>
            </Box>
          </Box>
          {medication.stopReason === 'Bristande effekt' && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  display: 'block',
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Stoppår:
              </Typography>
              <input
                type="text"
                onBlur={handleFormSubmit}
                value={medication.stopYear}
                onChange={(e) =>
                  handleInputChange(index, 'stopYear', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontFamily: DS.fontBody,
                }}
              />
            </Box>
          )}
          {medication.stopReason === 'Biverkan' && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  display: 'block',
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Ange biverkan: (max 10 ord)
              </Typography>
              <input
                type="text"
                onBlur={handleFormSubmit}
                value={medication.sideEffects}
                onChange={(e) =>
                  handleInputChange(index, 'sideEffects', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontFamily: DS.fontBody,
                }}
              />
              <Typography
                sx={{
                  display: 'block',
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Stoppår:
              </Typography>
              <input
                type="text"
                onBlur={handleFormSubmit}
                value={medication.stopYear}
                onChange={(e) =>
                  handleInputChange(index, 'stopYear', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontFamily: DS.fontBody,
                }}
              />
            </Box>
          )}
          {medication.stopReason === 'Annat skäl' && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  display: 'block',
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Ange skäl till avslut: (max 10 ord)
              </Typography>
              <input
                type="text"
                onBlur={handleFormSubmit}
                value={medication.stopReasonDetails}
                onChange={(e) =>
                  handleInputChange(index, 'stopReasonDetails', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontFamily: DS.fontBody,
                }}
              />
              <Typography
                sx={{
                  display: 'block',
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Stoppår:
              </Typography>
              <input
                type="text"
                onBlur={handleFormSubmit}
                value={medication.stopYear}
                onChange={(e) =>
                  handleInputChange(index, 'stopYear', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontFamily: DS.fontBody,
                }}
              />
            </Box>
          )}
        </Box>
      ))}
      <Box
        component="button"
        onClick={handleAddNewSet}
        sx={{
          mt: 2,
          py: 1.5,
          px: 2,
          backgroundColor: DS.deepTeal,
          color: '#FFFFFF',
          borderRadius: '6px',
          width: '100%',
          border: 'none',
          cursor: 'pointer',
          fontFamily: DS.fontBody,
          fontSize: '16px',
        }}
      >
        Lägg till ytterligare smärlindrande läkemedel
      </Box>
    </Box>
  );
};

export default HasAdditionalPreviousMedication;
